import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DropzoneArea } from 'material-ui-dropzone';
import { Avatar, Tooltip } from '@material-ui/core';
import { MuiThemeProvider } from '@material-ui/core/styles';
import DeleteRoundedIcon from '@material-ui/icons/DeleteRounded';
import { showError } from '../../services/notification';
import docIcon from '../../images/docIcon.png';
import xlsIcon from '../../images/xlsIcon.png';
import pptIcon from '../../images/pptIcon.png';
import pdfIcon from '../../images/pdfIcon.png';
import attachmentIcon from '../../images/defaultAttachmentIcon.png';
import { useStyles } from './style';
import { fileUploadTheme } from './theme';
import { DropzoneChipProps } from './dropzoneChipProps';
import EllipsisText from './elipsisText';
import { getFileExtension } from '../../utils/fileUtils';

interface Props {
  onChange: (file: File | undefined) => void;
  fileUrl?: string;
  keyValue: number;
  filesLimit?: number;
}

export function FileUpload(props: Props) {
  const fileUploadStyles = useStyles();
  const { t } = useTranslation();
  const [previewIcon, setPreviewIcon] = useState<string>();
  const [previewFileName, setPreviewFileName] = useState<string>('');
  const [labelWidth, setLabelWidth] = useState<number>(0);

  function handleInputChange(file: File[]) {
    if (file[0]) {
      setLabelWidth(file[0].name.length);
      setPreviewFileName(file[0].name);
      handleIconPreview(file[0].name);
      props.onChange(file[0]);
    }
  }

  function handleFileRejected(file: File[]) {
    showError(t('unsupported_file_type', { file: file[0].name }));
    props.onChange(undefined);
  }

  function handleFileRemovedFromDropzone(file: File) {
    props.onChange(undefined);
  }

  function handleIconPreview(fileName: string) {
    const fileExtension = getFileExtension(fileName);

    switch (fileExtension) {
      case '.doc':
        setPreviewIcon(docIcon);
        break;
      case '.docx':
        setPreviewIcon(docIcon);
        break;
      case '.xls':
        setPreviewIcon(xlsIcon);
        break;
      case '.xlsx':
        setPreviewIcon(xlsIcon);
        break;
      case '.ppt':
        setPreviewIcon(pptIcon);
        break;
      case '.pptx':
        setPreviewIcon(pptIcon);
        break;
      case '.pdf':
        setPreviewIcon(pdfIcon);
        break;
      default:
        setPreviewIcon(attachmentIcon);
        break;
    }
  }

  return (
    <DropzoneArea
      key={props.keyValue}
      dropzoneText={t('upload_file_dialog')}
      acceptedFiles={['.doc', '.docx', '.xls', '.xlsx', '.pdf', '.ppt', '.pptx']}
      filesLimit={props.filesLimit}
      maxFileSize={Number.MAX_VALUE}
      useChipsForPreview={true}
      previewChipProps={
        {
          variant: 'outlined',
          size: 'medium',
          label: <EllipsisText labelWidth={labelWidth}>{previewFileName}</EllipsisText>,
          className: fileUploadStyles.chip,
          icon: (
            <Avatar
              alt='attachmentIcon'
              src={previewIcon}
              className={fileUploadStyles.previewLarge}
              variant='rounded'
            />
          ),
          deleteIcon: (
            <Tooltip id='remove_file' title={t<string>('remove_selected')}>
              <span>
                <MuiThemeProvider theme={fileUploadTheme}>
                  <DeleteRoundedIcon color='action' />
                </MuiThemeProvider>
              </span>
            </Tooltip>
          ),
        } as DropzoneChipProps
      }
      showAlerts={false}
      onChange={handleInputChange}
      onDelete={handleFileRemovedFromDropzone}
      onDropRejected={handleFileRejected}
    />
  );
}
